import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CUSTOMERS_ENDPOINT = process.env.REACT_APP_CUSTOMERS_ENDPOINT || '/customers';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'moftah_auth_token';

// Debug configuration
console.log('Customer API Configuration:');
console.log('API URL:', API_URL);
console.log('CUSTOMERS ENDPOINT:', CUSTOMERS_ENDPOINT);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000 // 10 second timeout
});

// Add request interceptor to attach the token to all requests
api.interceptors.request.use(config => {
  console.log('Customer API Request:', config.method, config.url);
  
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Token attached to customer request');
  } else {
    console.log('No token found in localStorage for customer request');
  }
  
  return config;
}, error => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor for debugging
api.interceptors.response.use(response => {
  console.log('Customer API Response:', response.status, response.config.url);
  return response;
}, error => {
  console.error('Customer API Error:', error.response?.status, error.response?.data, error.config?.url);
  return Promise.reject(error);
});

// Get all customers
export const getAllCustomers = async () => {
  console.log('Calling getAllCustomers()');
  try {
    console.log(`Making request to: ${API_URL}${CUSTOMERS_ENDPOINT}`);
    const startTime = Date.now();
    const response = await api.get(`${CUSTOMERS_ENDPOINT}`);
    const timeElapsed = Date.now() - startTime;
    console.log(`Customers fetched in ${timeElapsed}ms:`, response.data.length);
    
    // Check if response data is valid
    if (!Array.isArray(response.data)) {
      console.error('Invalid response format - expected array but got:', typeof response.data);
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    if (error.code === 'ECONNABORTED') {
      console.error('Request timed out - server might be down or overloaded');
    } else if (!error.response) {
      console.error('Network error - check server connectivity');
    }
    throw error;
  }
};

// Get a single customer by ID
export const getCustomerById = async (customerId) => {
  try {
    const response = await api.get(`${CUSTOMERS_ENDPOINT}/${customerId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching customer ${customerId}:`, error);
    throw error;
  }
};

// Search customers by name or phone
export const searchCustomers = async (query) => {
  console.log(`Calling searchCustomers() with query: "${query}"`);
  try {
    console.log(`Making request to: ${API_URL}${CUSTOMERS_ENDPOINT}/search?query=${query}`);
    const startTime = Date.now();
    const response = await api.get(`${CUSTOMERS_ENDPOINT}/search`, {
      params: { query }
    });
    const timeElapsed = Date.now() - startTime;
    console.log(`Customer search completed in ${timeElapsed}ms:`, response.data.length, 'results');
    
    // Check if response data is valid
    if (!Array.isArray(response.data)) {
      console.error('Invalid search response format - expected array but got:', typeof response.data);
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error searching customers:', error);
    if (error.code === 'ECONNABORTED') {
      console.error('Search request timed out');
    } else if (!error.response) {
      console.error('Network error during search');
    }
    throw error;
  }
};

// Create a new customer
export const createCustomer = async (customerData) => {
  console.log('Calling createCustomer() with data:', customerData);
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      console.error('No authentication token found when creating customer');
      // Continue anyway, as customer creation might not require auth
    }
    
    const startTime = Date.now();
    const response = await api.post(`${CUSTOMERS_ENDPOINT}`, customerData);
    const timeElapsed = Date.now() - startTime;
    console.log(`Customer created in ${timeElapsed}ms:`, response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    if (error.response && error.response.data) {
      console.error('Server error details:', error.response.data);
    }
    throw error;
  }
};

// Update an existing customer
export const updateCustomer = async (customerId, customerData) => {
  try {
    const response = await api.put(`${CUSTOMERS_ENDPOINT}/${customerId}`, customerData);
    return response.data;
  } catch (error) {
    console.error(`Error updating customer ${customerId}:`, error);
    throw error;
  }
};

// Delete a customer
export const deleteCustomer = async (customerId) => {
  try {
    const response = await api.delete(`${CUSTOMERS_ENDPOINT}/${customerId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting customer ${customerId}:`, error);
    throw error;
  }
}; 